// HostedVideoPlayer.js
import React, { useRef, useEffect, useState, useImperativeHandle } from 'react';
import { getCachedVideo, putCachedVideo } from '../utils/videoCache';
import { useAuth } from '../context/AuthContext';
import { useT } from '../utils/i18n';
import '../App.css';

// Highest audio-boost multiplier the admin can cycle to (4x = 400%).
const MAX_BOOST_LEVEL = 4;

/**
 * Plays an S3/HTTP-hosted video and logs the narrator's play/pause/seek actions.
 *
 * When `preloadEnabled` is set, the whole file is downloaded in the background
 * first and the player is switched to a fully-local copy. That makes buffering
 * mid-recording impossible, so the video clock can't stall and drift the
 * sound-effect markers out of sync. The parent keeps recording locked until
 * `onDownloadComplete` (or `onDownloadError`, which falls back to streaming).
 */
const HostedVideoPlayer = ({
  videoUrl,
  videoId,
  onPlayerAction,
  preloadEnabled = false,
  onDownloadProgress,
  onDownloadComplete,
  onDownloadError,
  ref,
}) => {
  const videoRef = useRef(null);
  // True while we swap the <video> src programmatically, so the resulting
  // play/seeked DOM events are not logged as if the narrator caused them.
  const suppressEventsRef = useRef(false);
  // True for the whole duration of a reenact: the player is being driven by the
  // ReenactPanel, so every play/pause/seek is programmatic and must never be
  // logged (a narrator reenact would otherwise poison the overlay-processing
  // data). A persistent ref, unlike suppressEventsRef's setTimeout(0) release, is
  // essential because reenact fires a continuous stream of seeks whose `seeked`
  // events land asynchronously. While active, native controls are hidden and the
  // video is unmuted so the video audio is audible.
  const reenactActiveRef = useRef(false);
  const [reenactActive, setReenactActive] = useState(false);
  const savedAudioStateRef = useRef(null);
  const objectUrlRef = useRef(null);
  // The videoId we already hold a downloaded blob for, so a parent re-render
  // with a freshly presigned URL neither re-downloads nor reverts to streaming.
  const preloadedIdRef = useRef(null);
  const [downloadPct, setDownloadPct] = useState(0);
  const [downloadState, setDownloadState] = useState('idle'); // idle | downloading | done | error

  const { isAdmin } = useAuth();
  const t = useT();

  // Web Audio graph for the admin-only 200% boost. Built lazily on first toggle
  // (a MediaElementSource can be created only once per element, and starting an
  // AudioContext needs a user gesture), then kept for the element's lifetime.
  const audioCtxRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const gainNodeRef = useRef(null);
  // The gain the boost graph *should* currently hold, whether it was last set by
  // the admin boost button or the reenact video-volume slider. handleSeek re-
  // asserts from this (not from boostLevel) so seeking during a reenact doesn't
  // reset a slider-boosted video back to the admin button's 100%.
  const desiredGainRef = useRef(1);
  // The playback rate the video *should* be running at, set by the reenact speed
  // control. Held in a ref (not state) because every src change resets the
  // element's playbackRate to 1 and we re-assert from loadedmetadata.
  const desiredRateRef = useRef(1);
  // Volume multiplier the admin cycles through: 1x → 2x → 3x → 4x → back to 1x.
  const [boostLevel, setBoostLevel] = useState(1);
  // We request the stream with CORS (needed so Web Audio can read the samples
  // for the boost). If the bucket doesn't return CORS headers the media fails to
  // load; this one-shot flag lets us fall back to a plain load so review
  // playback still works (boost just won't amplify until the bucket is fixed).
  const corsFallbackTriedRef = useRef(false);

  // Set initial volume and unmute state on mount for all devices/platforms.
  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.volume = 1;
    videoRef.current.muted = false;
  }, []);

  // Keep the <video> source correct. src is managed here (not via a JSX prop) so
  // a new presigned URL from a parent refetch can't clobber the local blob.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (preloadEnabled && preloadedIdRef.current === videoId && objectUrlRef.current) {
      if (video.src !== objectUrlRef.current) video.src = objectUrlRef.current;
      return;
    }
    if (videoUrl && video.src !== videoUrl) {
      suppressEventsRef.current = true;
      video.src = videoUrl;
      setTimeout(() => { suppressEventsRef.current = false; }, 0);
    }
  }, [videoUrl, videoId, preloadEnabled, downloadState]);

  // Background download + swap to a local copy. On a repeat visit the blob is
  // served straight from the device cache (no network), so recording unlocks
  // instantly instead of re-downloading up to ~1 GB.
  useEffect(() => {
    if (!preloadEnabled || !videoUrl || !/^https?:/i.test(videoUrl)) return;
    if (preloadedIdRef.current === videoId) return; // already downloaded this video

    // The presigned signature rotates every fetch, but the object's path is
    // stable — use it to key the cache and to detect a swapped-out file.
    let s3Path = null;
    try { s3Path = new URL(videoUrl).pathname; } catch (e) { /* leave null */ }

    let cancelled = false;

    // Swap the <video> to a fully-local blob, restoring position/play state
    // without emitting fake log events. Shared by the cache-hit and freshly
    // -downloaded paths.
    const swapToBlob = (blob) => {
      const objectUrl = URL.createObjectURL(blob);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = objectUrl;
      preloadedIdRef.current = videoId;

      const video = videoRef.current;
      if (video) {
        const wasPaused = video.paused;
        const resumeAt = video.currentTime;
        suppressEventsRef.current = true;
        video.src = objectUrl;
        const restore = () => {
          video.removeEventListener('loadedmetadata', restore);
          try { video.currentTime = resumeAt || 0; } catch (e) { /* ignore */ }
          if (!wasPaused) video.play().catch(() => {});
          setTimeout(() => { suppressEventsRef.current = false; }, 0);
        };
        video.addEventListener('loadedmetadata', restore);
      }

      setDownloadPct(100);
      setDownloadState('done');
      if (onDownloadProgress) onDownloadProgress(100);
      if (onDownloadComplete) onDownloadComplete();
    };

    setDownloadState('downloading');
    setDownloadPct(0);
    if (onDownloadProgress) onDownloadProgress(0);

    (async () => {
      try {
        // Fast path: a current cached copy for this video.
        if (s3Path) {
          const cached = await getCachedVideo(videoId, s3Path);
          if (cancelled) return;
          if (cached) {
            swapToBlob(cached);
            return;
          }
        }

        const response = await fetch(videoUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const contentLength = Number(response.headers.get('Content-Length')) || 0;
        const reader = response.body.getReader();
        const chunks = [];
        let received = 0;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (cancelled) { reader.cancel(); return; }
          chunks.push(value);
          received += value.length;
          if (contentLength) {
            const pct = Math.min(99, Math.round((received / contentLength) * 100));
            setDownloadPct(pct);
            if (onDownloadProgress) onDownloadProgress(pct);
          }
        }
        if (cancelled) return;

        const blob = new Blob(chunks, { type: response.headers.get('Content-Type') || 'video/mp4' });
        // Persist for next time (fire-and-forget; a cache failure never breaks playback).
        if (s3Path) putCachedVideo(videoId, s3Path, blob).catch(() => {});
        swapToBlob(blob);
      } catch (err) {
        if (cancelled) return;
        console.error('Video preload failed; falling back to streaming playback:', err);
        setDownloadState('error');
        if (onDownloadError) onDownloadError(err);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadEnabled, videoId, videoUrl]);

  // Release the object URL and tear down the audio graph when the player unmounts.
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
    };
  }, []);

  const handlePlay = () => {
    if (suppressEventsRef.current || reenactActiveRef.current) return;
    onPlayerAction('play', videoRef.current.currentTime);
  };

  const handlePause = () => {
    if (suppressEventsRef.current || reenactActiveRef.current) return;
    onPlayerAction('pause', videoRef.current.currentTime);
  };

  const handleSeek = () => {
    // Some browsers drop the Web Audio level/routing across a seek, which reads
    // as the boost switching itself off. Re-assert the gain (and wake the
    // context) so boost survives seeking.
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = desiredGainRef.current;
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
    }
    if (suppressEventsRef.current || reenactActiveRef.current) return;
    onPlayerAction('seek', videoRef.current.currentTime);
  };

  // Control surface handed to the ReenactPanel (React 19 ref-as-prop). The raw
  // <video> element stays private; the panel drives it through these methods so
  // the player keeps ownership of suppression, the controls attribute, and mute.
  useImperativeHandle(ref, () => ({
    beginReenact: () => {
      const video = videoRef.current;
      reenactActiveRef.current = true;
      setReenactActive(true);
      if (video) {
        savedAudioStateRef.current = { muted: video.muted, volume: video.volume };
        // The reenact must always be audible (video audio plays under the narration).
        video.muted = false;
        video.volume = 1;
      }
    },
    endReenact: () => {
      const video = videoRef.current;
      reenactActiveRef.current = false;
      setReenactActive(false);
      if (video && savedAudioStateRef.current) {
        video.muted = savedAudioStateRef.current.muted;
        video.volume = savedAudioStateRef.current.volume;
      }
      // Hand the boost graph back to whatever the admin button last set, so
      // plain playback isn't left amplified by the reenact slider.
      desiredGainRef.current = boostLevel;
      if (gainNodeRef.current) gainNodeRef.current.gain.value = boostLevel;
      // Likewise the speed: plain playback must never be left running at 3x.
      desiredRateRef.current = 1;
      if (video) video.playbackRate = 1;
      savedAudioStateRef.current = null;
    },
    // Run the video at the reenact's playback rate. The narration audio and the
    // video must share one rate or the segment map's media-time arithmetic
    // (position = anchorPos + elapsed audio) stops holding.
    setPlaybackRate: (rate) => {
      const video = videoRef.current;
      if (!Number.isFinite(rate) || rate <= 0) return;
      desiredRateRef.current = rate;
      if (video) video.playbackRate = rate;
    },
    // Set the video's playback volume as a 0..4 multiplier (0–400%). Up to 1x is
    // a plain element attenuation; above 1x routes through the same gain+limiter
    // graph as the admin boost button (needs the CORS'd element, hence the
    // always-on crossOrigin below). Falls back to the element's 0..1 range if the
    // graph can't be built.
    setVideoVolume: (level) => {
      const video = videoRef.current;
      if (!video) return;
      const v = Math.max(0, level);
      if (v <= 1 && !sourceNodeRef.current) {
        video.volume = v;
        return;
      }
      if (ensureAudioGraph()) {
        if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume().catch(() => {});
        desiredGainRef.current = v;
        gainNodeRef.current.gain.value = v;
        video.volume = 1;
      } else {
        video.volume = Math.min(1, v);
      }
    },
    play: () => {
      const video = videoRef.current;
      if (video) return video.play().catch(() => {});
      return Promise.resolve();
    },
    pause: () => {
      if (videoRef.current) videoRef.current.pause();
    },
    seekTo: (seconds) => {
      const video = videoRef.current;
      if (video && Number.isFinite(seconds)) {
        video.currentTime = Math.max(0, seconds);
      }
    },
    getState: () => {
      const video = videoRef.current;
      if (!video) return null;
      return {
        currentTime: video.currentTime,
        paused: video.paused,
        duration: video.duration,
        readyState: video.readyState,
      };
    },
  }));

  // If the *initial* CORS-enabled load fails (bucket without CORS headers), retry
  // once without crossOrigin so the admin can still watch — boost is sacrificed,
  // not playback. Guard on readyState so a later, transient error (e.g. a
  // range-request blip while seeking) never strips crossOrigin and reloads the
  // element, which would orphan the boost's audio graph.
  // Every src change (the preload blob swap, the CORS fallback reload, a freshly
  // presigned URL) resets playbackRate to 1. Re-assert the reenact's rate here so
  // a speed set before the swap survives it.
  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (video && video.playbackRate !== desiredRateRef.current) {
      video.playbackRate = desiredRateRef.current;
    }
  };

  const handleVideoError = () => {
    const video = videoRef.current;
    if (!video || corsFallbackTriedRef.current) return;
    if (video.crossOrigin && video.readyState === 0) {
      corsFallbackTriedRef.current = true;
      const src = video.src;
      video.removeAttribute('crossorigin');
      suppressEventsRef.current = true;
      video.src = src;
      video.load();
      setTimeout(() => { suppressEventsRef.current = false; }, 0);
    }
  };

  // Build the source → gain → limiter → destination graph once.
  // createMediaElementSource throws if called twice on the same element, so
  // guard on sourceNodeRef. The limiter (a DynamicsCompressor set to catch only
  // peaks) lets the gain raise perceived loudness cleanly instead of hard-
  // clipping once the signal hits full scale — without it, 200/300/400% mostly
  // just distort and feel nearly identical.
  const ensureAudioGraph = () => {
    if (sourceNodeRef.current) return true;
    const video = videoRef.current;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!video || !AudioCtx) return false;
    try {
      const ctx = new AudioCtx();
      const source = ctx.createMediaElementSource(video);
      const gain = ctx.createGain();
      gain.gain.value = 1.0;
      const limiter = ctx.createDynamicsCompressor();
      limiter.threshold.value = -6;   // start taming peaks a touch below 0 dBFS
      limiter.knee.value = 6;
      limiter.ratio.value = 20;       // brick-wall-ish: peaks don't clip
      limiter.attack.value = 0.003;
      limiter.release.value = 0.25;
      source.connect(gain).connect(limiter).connect(ctx.destination);
      audioCtxRef.current = ctx;
      sourceNodeRef.current = source;
      gainNodeRef.current = gain;
      return true;
    } catch (err) {
      console.error('Audio boost unavailable:', err);
      return false;
    }
  };

  const cycleBoost = () => {
    const next = boostLevel >= MAX_BOOST_LEVEL ? 1 : boostLevel + 1;
    if (!ensureAudioGraph()) return;
    // The click is the user gesture that lets a suspended context start.
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume().catch(() => {});
    desiredGainRef.current = next;
    gainNodeRef.current.gain.value = next;
    setBoostLevel(next);
  };

  const isDownloading = downloadState === 'downloading';

  return (
    <div className="hosted-video-player mb-3">
      <h4>{t('Video Preview')}</h4>
      <div className="responsive-video-container">
        <video
          ref={videoRef}
          controls={!reenactActive}
          // Always CORS so the reenact video-volume boost (and the admin boost)
          // can read the samples for anyone; handleVideoError drops it and
          // reloads plain if a bucket lacks CORS headers.
          crossOrigin="anonymous"
          onPlay={handlePlay}
          onPause={handlePause}
          onSeeked={handleSeek}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleVideoError}
          className="responsive-video"
        >
          {t('Your browser does not support the video tag.')}
        </video>
      </div>
      {isAdmin && (
        // The button's accessible name carries the current level, so the screen
        // reader announces it when the button is activated — no separate live
        // region, which was pulling the browse cursor off the media controls.
        <button
          type="button"
          className={`btn btn-sm mt-2 ${boostLevel > 1 ? 'btn-warning' : 'btn-outline-secondary'}`}
          onClick={cycleBoost}
          aria-label={`Audio boost ${boostLevel * 100} percent. Activate to increase.`}
          title={`Cycle the video's volume: 100% → 200% → 300% → ${MAX_BOOST_LEVEL * 100}%`}
        >
          <span aria-hidden="true">🔊 Boost: {boostLevel * 100}%</span>
        </button>
      )}
      {isDownloading && (
        <div className="mt-2">
          <small className="text-muted d-block mb-1">
            กำลังโหลดวิดีโอทั้งหมดเพื่อป้องกันการกระตุก... {downloadPct}%
          </small>
          <div className="progress" style={{ height: '15px' }}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${downloadPct}%` }}
              aria-valuenow={downloadPct}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {downloadPct}%
            </div>
          </div>
        </div>
      )}
      {downloadState === 'error' && (
        <div className="alert alert-warning mt-2 mb-0 py-2 small">
          โหลดวิดีโอล่วงหน้าไม่สำเร็จ กำลังเล่นแบบสตรีมแทน (อาจมีการกระตุกได้)
        </div>
      )}
    </div>
  );
};

export default HostedVideoPlayer;
